import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { RotateCcw } from 'lucide-react';

import scss from './CommonSlider.module.scss';

const DEFAULT_THROTTLE_MS = 80;

function CommonSlider({
  label,
  icon: Icon,
  value,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  onCommit,
  disabled = false,
  valueFormatter,
  className,
  resetValue,
  resetLabel,
  throttleMs = DEFAULT_THROTTLE_MS,
}) {
  const [localValue, setLocalValue] = useState(value);
  const draggingRef = useRef(false);
  const latestRef = useRef(value);
  const emittedRef = useRef(value);
  const timerRef = useRef(0);
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);
  const windowListeningRef = useRef(false);

  onChangeRef.current = onChange;
  onCommitRef.current = onCommit;

  const emitChange = (next) => {
    if (emittedRef.current === next) {
      return;
    }
    emittedRef.current = next;
    onChangeRef.current?.(next);
  };

  const stopWindowListeners = useRef(() => {});
  const handleCommit = useRef(() => {});

  handleCommit.current = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = 0;
    }

    const wasDragging = draggingRef.current;
    draggingRef.current = false;
    stopWindowListeners.current();

    if (!wasDragging) {
      return;
    }

    emitChange(latestRef.current);
    onCommitRef.current?.(latestRef.current);
  };

  const onWindowCommit = useRef((event) => {
    handleCommit.current(event);
  });

  stopWindowListeners.current = () => {
    if (!windowListeningRef.current) {
      return;
    }
    window.removeEventListener('pointerup', onWindowCommit.current);
    window.removeEventListener('mouseup', onWindowCommit.current);
    window.removeEventListener('touchend', onWindowCommit.current);
    windowListeningRef.current = false;
  };

  useEffect(() => {
    if (!draggingRef.current) {
      setLocalValue(value);
      latestRef.current = value;
      emittedRef.current = value;
    }
  }, [value]);

  useEffect(
    () => () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      stopWindowListeners.current();
    },
    [],
  );

  const displayValue = valueFormatter ? valueFormatter(localValue) : localValue;

  const scheduleChange = (next) => {
    latestRef.current = next;
    if (timerRef.current) {
      return;
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = 0;
      emitChange(latestRef.current);
    }, Math.max(0, throttleMs));
  };

  const startWindowListeners = () => {
    if (windowListeningRef.current) {
      return;
    }
    window.addEventListener('pointerup', onWindowCommit.current);
    window.addEventListener('mouseup', onWindowCommit.current);
    window.addEventListener('touchend', onWindowCommit.current);
    windowListeningRef.current = true;
  };

  const handleChange = (event) => {
    const next = Number(event.target.value);
    draggingRef.current = true;
    startWindowListeners();
    setLocalValue(next);
    scheduleChange(next);
  };

  const handleReset = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || resetValue == null) {
      return;
    }

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = 0;
    }

    draggingRef.current = false;
    stopWindowListeners.current();
    setLocalValue(resetValue);
    latestRef.current = resetValue;
    emitChange(resetValue);
    onCommitRef.current?.(resetValue);
  };

  const canReset = resetValue != null && !disabled;
  const isModified = canReset && Number(localValue) !== Number(resetValue);
  const progress = max > min ? ((Number(localValue) - min) / (max - min)) * 100 : 0;

  return (
    <div className={clsx(scss.slider, disabled && scss.disabled, className)}>
      <div className={scss.header}>
        {Icon ? <Icon size={14} strokeWidth={2} className={scss.icon} aria-hidden /> : null}
        {label ? <span className={scss.label}>{label}</span> : null}
        <button
          type='button'
          className={clsx(scss.reset, isModified && scss.resetVisible)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleReset}
          disabled={!isModified}
          tabIndex={isModified ? 0 : -1}
          aria-hidden={!isModified}
          aria-label={resetLabel}
          title={resetLabel}>
          <RotateCcw size={11} />
        </button>
        <span className={clsx(scss.value, isModified && scss.valueModified)}>{displayValue}</span>
      </div>
      <input
        className={scss.input}
        type='range'
        min={min}
        max={max}
        step={step}
        value={localValue}
        disabled={disabled}
        style={{ '--slider-progress': `${Math.min(100, Math.max(0, progress))}%` }}
        onChange={handleChange}
        onDoubleClick={canReset ? handleReset : undefined}
        onMouseUp={() => handleCommit.current()}
        onTouchEnd={() => handleCommit.current()}
        onKeyUp={() => handleCommit.current()}
        aria-label={typeof label === 'string' ? label : undefined}
      />
    </div>
  );
}

export default CommonSlider;
